import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';

/**
 * TanStack Query Client Configuration
 *
 * Features:
 * - Global error handling via QueryCache/MutationCache
 * - Error boundary support (throws on 5xx errors)
 * - Optimized caching (5min stale, 30min gc)
 * - Exponential backoff retry
 *
 * Suspense Usage:
 * TanStack Query v5 provides built-in suspense hooks. Use these for
 * React 19 Suspense integration:
 *
 * @example
 * ```tsx
 * import { useSuspenseQuery } from '@tanstack/react-query';
 *
 * function CourseDetails({ id }: { id: string }) {
 *   // This will suspend until data is ready - no loading state needed!
 *   const { data } = useSuspenseQuery({
 *     queryKey: queryKeys.courses.detail(id),
 *     queryFn: () => fetchCourse(id),
 *   });
 *
 *   return <div>{data.name}</div>;
 * }
 *
 * // Wrap with Suspense boundary
 * <Suspense fallback={<Skeleton />}>
 *   <CourseDetails id="123" />
 * </Suspense>
 * ```
 */

/**
 * Global error handler for query errors.
 * This can be used to show toast notifications or log to error tracking.
 */
const handleQueryError = (error: Error) => {
  // Log to console in development
  if (import.meta.env.DEV) {
    console.error('[Query Error]', error.message);
  }

  // TODO: Add error tracking (e.g., Sentry) in production
  // TODO: Add toast notification for user-facing errors
};

/**
 * Global error handler for mutation errors.
 */
const handleMutationError = (error: Error) => {
  if (import.meta.env.DEV) {
    console.error('[Mutation Error]', error.message);
  }

  // TODO: Add error tracking in production
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  mutationCache: new MutationCache({
    onError: handleMutationError,
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      // Enable error boundaries for critical queries
      // Individual queries can override with throwOnError: false
      throwOnError: (error) => {
        // Only throw to error boundary for server errors (5xx)
        // Client errors (4xx) are handled in components
        const status = (error as { status?: number }).status;
        return status !== undefined && status >= 500;
      },
    },
    mutations: {
      retry: 1,
      // Mutations throw errors by default for try/catch handling
      throwOnError: false,
    },
  },
});
